<script lang="ts">

  import { page } from "$app/state"
  import { Button } from "$lib/components/ui/button"
  import { Label } from "$lib/components/ui/label"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { cn } from "$lib/utils"
  import ArrowUp10Icon from "@lucide/svelte/icons/arrow-up-10"
  import BookTextIcon from "@lucide/svelte/icons/book-text"
  import Building2Icon from "@lucide/svelte/icons/building-2"
  import KeySquareIcon from "@lucide/svelte/icons/key-square"
  import LogOutIcon from "@lucide/svelte/icons/log-out"
  import ShieldIcon from "@lucide/svelte/icons/shield"
  import SquareArrowOutUpRightIcon from "@lucide/svelte/icons/square-arrow-out-up-right"
  import UserRoundIcon from "@lucide/svelte/icons/user-round"
  import Navigation from "./../_components/navigation.svelte"
  import type { Snippet } from "svelte"
  import { toast } from "svelte-sonner"
  import DialogApplicationInformation from "./_components/dialog-application-information.svelte"
  import DialogChangePassword from "./_components/dialog-change-password.svelte"
  import DialogLogoutConfirm from "./_components/dialog-logout-confirm.svelte"
  import DialogProfile from "./_components/dialog-profile.svelte"

  const prefix = $derived(page.params.prefix ?? "0")

  const profileDialog = useDialog()
  const passwordDialog = useDialog()
  const applicationInformationDialog = useDialog()
  const logoutDialog = useDialog()

  function updateApplication() {

    if (typeof _device === "undefined") {
      toast.info("Gagal update", {
        description: "Perangkat tidak terdaftar.",
      })
      return
    }

    try {
      _device.appUpdate()
    } catch (error) {
      toast.info("Gagal update", {
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

</script>

{#snippet IconProfile()}
  <UserRoundIcon class="size-6" aria-hidden="true"/>
{/snippet}

{#snippet IconPassword()}
  <KeySquareIcon class="size-6" aria-hidden="true"/>
{/snippet}

{#snippet IconApplicationInformation()}
  <BookTextIcon class="size-6" aria-hidden="true"/>
{/snippet}

{#snippet IconContact()}
  <Building2Icon class="size-6" aria-hidden="true"/>
{/snippet}

{#snippet IconUpdate()}
  <ArrowUp10Icon class="size-6" aria-hidden="true"/>
{/snippet}

{#snippet IconLogout()}
  <LogOutIcon class="size-6" aria-hidden="true"/>
{/snippet}

{#snippet ButtonAction(
  icon: Snippet,
  title: string,
  description: string,
  action: () => void,
  destructive = false,
)}
  <Button
    variant="ghost"
    type="button"
    class={cn(
      "flex h-auto w-full items-start gap-4 rounded-lg px-4 py-3 text-start hover:bg-muted",
      destructive ? "text-destructive" : "",
    )}
    onclick={action}
  >
    <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center">
      {@render icon()}
    </span>
    <span class="min-w-0 flex-1">
      <span class="block font-medium">{title}</span>
      {#if description}
        <span class="block break-words whitespace-normal text-xs leading-4 {destructive ? "text-destructive" : "text-muted-foreground"}">
          {description}
        </span>
      {/if}
    </span>
  </Button>
{/snippet}

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <h1 class="text-lg font-medium">Pengaturan</h1>
      </div>
    </div>
  </nav>
  <div class="space-y-4">
    <div></div>
    <section class="px-3" aria-labelledby="account-setting-title">
      <Label
        id="account-setting-title"
        class="mb-2 text-sm font-medium leading-none text-muted-foreground">
        Akun
      </Label>
      {@render ButtonAction(
        IconProfile,
        "Profil",
        "Informasi user yang sedang digunakan.",
        () => profileDialog.setOpen(true),
      )}
      {@render ButtonAction(
        IconPassword,
        "Ubah password",
        "Untuk menjaga keamanan, ubah password secara berkala.",
        () => passwordDialog.setOpen(true),
      )}
    </section>
    <section class="px-3" aria-labelledby="information-setting-title">
      <Label
        id="information-setting-title"
        class="mb-2 text-sm font-medium leading-none text-muted-foreground">
        Informasi
      </Label>
      {@render ButtonAction(
        IconApplicationInformation,
        "Informasi aplikasi",
        "Informasi yang membantu user dan tim Support.",
        () => applicationInformationDialog.setOpen(true),
      )}
      <a
        href="https://sinergics.net/privacy"
        target="_blank"
        rel="noopener noreferrer"
        class="flex w-full items-start gap-4 rounded-lg border border-transparent px-4 py-3 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center">
          <ShieldIcon class="size-6" aria-hidden="true"/>
        </span>
        <span class="min-w-0 flex-1">
          <span class="block font-medium">
            Kebijakan privasi
            <SquareArrowOutUpRightIcon class="inline-block size-3" aria-hidden="true"/>
          </span>
          <span class="block text-xs leading-4 text-muted-foreground">
            Komitmen pengembang untuk melindungi informasi pribadi pengguna.
          </span>
        </span>
      </a>
      <a
        href={`/${prefix}/setting/contact`}
        class="flex w-full items-start gap-4 rounded-lg border border-transparent px-4 py-3 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <span class="mt-0.5 flex size-6 shrink-0 items-center justify-center">
          {@render IconContact()}
        </span>
        <span class="min-w-0 flex-1">
          <span class="block font-medium">Kontak</span>
          <span class="block text-xs leading-4 text-muted-foreground">
            Daftar informasi kontak yang dapat dihubungi.
          </span>
        </span>
      </a>
    </section>

    <section class="px-3" aria-labelledby="application-setting-title">
      <Label
        id="application-setting-title"
        class="mb-2 text-sm font-medium leading-none text-muted-foreground">
        Aplikasi
      </Label>
      {@render ButtonAction(
        IconUpdate,
        "Update aplikasi",
        "Periksa pembaruan jika notifikasi Play Store tidak muncul.",
        updateApplication,
      )}
      {@render ButtonAction(
        IconLogout,
        "Keluar akun",
        "Akun ini akan dikeluarkan dari aplikasi.",
        () => logoutDialog.setOpen(true),
        true,
      )}
    </section>
  </div>
  <div class="h-16"></div>
  <Navigation/>
</main>

<DialogProfile
  open={profileDialog.state}
  setOpen={profileDialog.setOpen}
/>
<DialogChangePassword
  open={passwordDialog.state}
  setOpen={passwordDialog.setOpen}
/>
<DialogApplicationInformation
  open={applicationInformationDialog.state}
  setOpen={applicationInformationDialog.setOpen}
/>
<DialogLogoutConfirm
  open={logoutDialog.state}
  setOpen={logoutDialog.setOpen}
/>
